import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import tenantPaymentService from '../services/tenantPaymentService';
import { track } from '../observability';
import TenantImpersonationBanner from '../components/TenantImpersonationBanner';
import StripeTrustBadge from '../components/StripeTrustBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';
const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

// Inner form — rendered once we have a client secret + connected account.
const PayForm = ({ amount, onDone, attemptId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const report = (outcome, detail) =>
    tenantPaymentService.reportPayAttempt(attemptId, outcome, detail);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    // Which methods the form offers is the facility's Stripe dashboard
    // setting (2026-10-20). A redirect-based one, if the operator enables it,
    // needs somewhere to come back to; without return_url Stripe refuses the
    // confirm outright. The webhook moves the balance either way.
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    });

    if (confirmError) {
      report('failed', confirmError.message || confirmError.code);
      setError(confirmError.message || 'Payment failed. Please try again.');
      setSubmitting(false);
      return;
    }

    // Inspect the PaymentIntent status — without this the button hangs on
    // "Processing…" forever when the intent is left pending (processing /
    // requires_action / requires_payment_method) rather than succeeded.
    const status = paymentIntent?.status;
    if (status === 'succeeded' || status === 'processing') {
      // succeeded → done; processing (e.g. Cash App settling) → also done,
      // the webhook moves the balance when it confirms.
      report(status === 'succeeded' ? 'succeeded' : 'processing');
      onDone();
      return;
    }
    if (status === 'requires_payment_method') {
      report('failed', 'requires_payment_method');
      setError('That payment didn’t go through. Please try another card.');
      setSubmitting(false);
      return;
    }
    // requires_action / requires_confirmation / anything unexpected — surface
    // it instead of spinning silently.
    report('failed', `status:${status || 'unknown'}`);
    setError(
      `Payment could not be completed (status: ${status || 'unknown'}). `
      + 'Please try again or contact the facility.',
    );
    setSubmitting(false);
  };

  // The card form mounting is the "form loaded" signal — the friction Kim hit
  // was the form NEVER loading (stuck on Processing), so record when it does.
  const onFormReady = () => report('form_loaded');

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Display order: card first, then bank (ACH — only appears when the
          facility's ach_enabled toggle is ON; the backend pins the intent's
          payment_method_types, so the Stripe Dashboard alone can't surface
          it), then Cash App Pay / Link (Apple/Google Pay). */}
      <PaymentElement
        onReady={onFormReady}
        options={{ paymentMethodOrder: ['card', 'us_bank_account', 'cashapp'] }}
      />
      <StripeTrustBadge size="standard" />
      {error && <p className="text-[13px] text-red-500">{error}</p>}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full rounded-lg bg-blue-600 text-white text-[13px] font-medium py-2.5 hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {submitting ? 'Processing…' : `Pay $${amount.toFixed(2)}`}
      </button>
    </form>
  );
};

const PortalPay = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const rentalId = params.get('rental');
  const payAll = params.get('payAll') === '1';
  const presetAmount = parseFloat(params.get('amount') || '0');

  const [amount, setAmount] = useState(presetAmount > 0 ? presetAmount : '');
  const [clientSecret, setClientSecret] = useState(null);
  const [connectedAccount, setConnectedAccount] = useState(null);
  const [facilityPublishableKey, setFacilityPublishableKey] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [facilityName, setFacilityName] = useState(null);

  useEffect(() => {
    if (!rentalId && !payAll) navigate('/portal/dashboard');
  }, [rentalId, payAll, navigate]);

  // Surface the facility name in the page header so the tenant knows
  // which operator they're paying. /api/tenant/me is scoped to the
  // caller's session — no cross-facility leakage.
  useEffect(() => {
    const token = localStorage.getItem('tenantAccessToken');
    if (!token) return;
    axios
      .get(`${API_URL}/api/tenant/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setFacilityName(res.data?.facility_name || null))
      .catch(() => { /* header is best-effort; payment flow doesn't depend on it */ });
  }, []);

  // Stripe init. Each facility is a SEPARATE standalone Stripe account, so when
  // the backend returns that facility's own publishable key, initialize
  // Stripe.js with it directly (the pk + the charge's sk are from the same
  // account — no {stripeAccount} needed). Only when a facility has NO per-
  // facility key do we fall back to the legacy shared platform key +
  // {stripeAccount} (Connect-style) path. This keeps facilities on the old
  // shared key working unchanged while letting per-account facilities use their
  // own key.
  const stripePromise = useMemo(() => {
    if (facilityPublishableKey) {
      return loadStripe(facilityPublishableKey);
    }
    if (!connectedAccount || !PUBLISHABLE_KEY) return null;
    return loadStripe(PUBLISHABLE_KEY, { stripeAccount: connectedAccount });
  }, [facilityPublishableKey, connectedAccount]);

  // Telemetry: intent created but Stripe couldn't initialize (no publishable
  // key / connected account) → the card form never renders. This is exactly
  // the friction a tenant experiences as a stuck "Processing" — capture it.
  useEffect(() => {
    if (clientSecret && !stripePromise && attemptId) {
      tenantPaymentService.reportPayAttempt(
        attemptId, 'form_not_loaded',
        !PUBLISHABLE_KEY && !facilityPublishableKey
          ? 'no publishable key'
          : !connectedAccount ? 'no connected account' : 'stripe init failed',
      );
    }
  }, [clientSecret, stripePromise, attemptId, facilityPublishableKey, connectedAccount]);

  const startPayment = async () => {
    // Pay-all: the backend computes the total from the tenant's balances, so
    // no client amount is validated/sent. Single-rental keeps the amount check.
    const amt = parseFloat(amount);
    if (!payAll && (!amt || amt <= 0)) {
      setError('Enter a valid amount.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = payAll
        ? await tenantPaymentService.createPayAllIntent()
        : await tenantPaymentService.createPayIntent(rentalId, amt);
      setClientSecret(res.data.client_secret);
      setConnectedAccount(res.data.connected_account);
      setAttemptId(res.data.attempt_id || null);
      // Per-facility publishable key (standalone account). Null -> fall back
      // to the shared platform key in stripePromise above.
      setFacilityPublishableKey(res.data.publishable_key || null);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        navigate('/portal');
        return;
      }
      setError(err.response?.data?.detail || 'Could not start payment.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex flex-col bg-[#FAFBFC]">
        <TenantImpersonationBanner />
        <header className="bg-white border-b border-slate-200/80">
          <div className="max-w-5xl mx-auto px-4 py-3">
            <h1 className="text-[15px] font-semibold text-slate-900">{facilityName || 'Tenant Portal'}</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center">
        <div className="text-center mt-4">
          <p className="text-[15px] font-semibold text-slate-900">Payment submitted</p>
          <p className="text-[13px] text-slate-500 mt-1">
            Your balance updates once the payment confirms.
          </p>
          <button
            onClick={() => navigate('/portal/dashboard')}
            className="mt-4 text-[13px] text-blue-600 hover:underline"
          >
            Back to dashboard
          </button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      <TenantImpersonationBanner />
      <header className="bg-white border-b border-slate-200/80">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <h1 className="text-[15px] font-semibold text-slate-900">{facilityName || 'Tenant Portal'}</h1>
        </div>
      </header>
      <div className="max-w-md mx-auto px-4 py-10">
        <button
          onClick={() => navigate('/portal/dashboard')}
          className="text-[13px] text-slate-400 hover:text-slate-600 mb-4"
        >
          ← Back
        </button>
        <div className="rounded-xl border border-slate-200/80 bg-white p-6">
          <h1 className="text-[15px] font-semibold text-slate-900 mb-4">Make a payment</h1>

          {!clientSecret ? (
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] font-medium text-slate-500 mb-1">
                  Amount (USD)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
              {error && <p className="text-[13px] text-red-500">{error}</p>}
              <button
                onClick={startPayment}
                disabled={loading}
                className="w-full rounded-lg bg-blue-600 text-white text-[13px] font-medium py-2.5 hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Starting…' : 'Continue to payment'}
              </button>
            </div>
          ) : !stripePromise ? (
            // We have a client_secret but Stripe couldn't initialize against
            // the connected account (missing publishable key or connected
            // account). Surface it instead of showing an empty card shell
            // that looks stuck on "Processing".
            <p className="text-[13px] text-amber-600">
              Payment form could not load
              {!PUBLISHABLE_KEY
                ? ' — online payments are not configured for this site.'
                : !connectedAccount
                ? ' — this facility’s payment account is not connected.'
                : '. Please refresh and try again.'}
            </p>
          ) : (
            (
              <Elements
                stripe={stripePromise}
                options={{ clientSecret, appearance: { theme: 'stripe' } }}
              >
                <PayForm
                  amount={parseFloat(amount)}
                  attemptId={attemptId}
                  onDone={() => {
                    track('tenant_payment_submitted', { rental_id: rentalId });
                    setDone(true);
                  }}
                />
              </Elements>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalPay;
